import CRMManager from '../crm';
import apiV4 from '../modules/api.js';
import { getCookie, getOrdinalNumber } from '../helpers';

const DISPLAY_LOGS = localStorage.getItem('segment_logs') === 'true';

const HubSpotVariables = {
	lead_type: {
		B2B: 'B2B',
		SS: 'Self Service',
	},
	customer_status: {
		active: 'Active',
		notActive: 'Not Active',
		churned: 'Churned',
		reactivated: 'Reactivated',
	},
};

class analytics {
	/*  HubSpot Date custom fields */
	// entered_credit_card
	// last_login_date
	// create_studio
	// download_files_from_dashboard
	// made_a_recording
	// sign_up
	// send_out_an_invitation
	// trial_end_date_
	// integration_contact

	email = undefined;

	_getGlobal() {
		return (this.analytics = window.analytics);
	}

	// IMPORTANT: Keep this in line with the same method in the BE
	_defaultIdentify(user, account) {
		const trial_end_date_ = account && account.trialExpiration ? new Date(account.trialExpiration) : null;

		const ident = {
			name: user.name,
			email: user.email,
			customer_status: HubSpotVariables.customer_status[user.customer_status],
			created_at: new Date(user.created),
			isEnterprise: user.enterprise || account?.isEnterprise,
			isOldEnterprise: user.enterprise && !account?.isEnterprise,
			lead_type: account?.isEnterprise ? HubSpotVariables.lead_type.B2B : HubSpotVariables.lead_type.SS,
			accountId: account ? account._id : null,
			groupId: account ? account._id : null,
			trial_end_date_,
			provider: user.provider || null,
			gclid: user.gclid || null,
			acquisitionSource: user.acquisitionSource || null,
			acquisitionSourceDate: user.acquisitionSourceDate || null,
			website: account?.metaData?.details?.url || null,
			purpose: account?.metaData?.purpose || null,
			useType: account?.metaData?.useType || null,
			social: account?.metaData?.details?.destination || null,
			onBoardingDate: account?.metaData?.createdAt || null,
		};

		if (account.plan) {
			ident.plan = account.plan;
		}

		// Only set accountOwner if it's a new Enterprise client, otherwise, skip it.
		if (!ident.isOldEnterprise) {
			ident.isAccountOwner = account && String(user._id) === String(account.owner);
		}

		this.email = user.email;
		this.name = user.name;

		return ident;
	}

	async page() {
		return this._getGlobal()?.page.apply(this, arguments);
	}

	async identify() {
		return this._getGlobal()?.identify.apply(this, arguments);
	}

	async reset() {
		return this._getGlobal()?.reset.apply(this, arguments);
	}

	async track() {
		const defaultProperties = {};
		if (arguments[1]) {
			Object.assign(arguments[1], defaultProperties);
		} else {
			arguments[1] = defaultProperties;
		}

		//console.log('[tracking]', arguments);
		return this._getGlobal()?.track.apply(this, arguments);
	}

	async screen() {
		return this._getGlobal()?.screen.apply(this, arguments);
	}

	/* User related events */

	async userSignedUp(user, account, referral) {
		// we moved this to th backend
	}

	async userSignedOut() {
		await this.track('User Signed Out');
		this.reset();
		if (CRMManager.isActive()) {
			CRMManager.shutDown();
		}
	}

	updateMe(user, account) {
		const defaultUser = this._defaultIdentify(user, account);

		return this.identify(user._id, {
			...defaultUser,
		});
	}

	async userSignedIn(user, account) {
		const defaultUser = this._defaultIdentify(user, account);

		await this.identify(user._id, {
			...defaultUser,
			last_login_date: new Date(),
		});

		return this.track('User Signed In', {
			email: user.email,
			groupId: account ? account._id : null,
			accountId: account ? account._id : null,
		});
	}

	async supportLogIn(user, account) {
		return this.track('Support Log In', {
			email: user.email,
			userId: user._id,
			groupId: account ? account._id : null,
			accountId: account ? account._id : null,
		});
	}

	async pricingPage(trigger) {
		return this.track('enter_pricing_page', {
			trigger,
		});
	}

	onboardingQuestionViewed(userId, email, question) {
		return this.track(`Onboarding question ${question} viewed`, {
			email,
			userId,
			question,
		});
	}

	/* Studio related events */

	async notificationPermissionsRequestResult(result, origin) {
		await this.track('Notification Permissions Requested', { result, origin });
	}

	async studioCreated(studioId, slug, title, hasVideo = true, now = true, invites = []) {
		//Test event in hubspot - pe9154210_studio_created
		await this.track(
			'Studio Created',
			{
				studioId,
				eventId: 'pe9154210_new_studio_created',
				slug,
				title,
				type: hasVideo ? 'Audio & Video' : 'Audio only',
				hasVideo,
				when: now ? 'now' : 'later',
			},
			{
				context: {
					traits: {
						create_studio: new Date(),
					},
				},
			}
		);

		await this.trackProxiedEvent(
			'studio_creation',
			{},
			{ trackAfterNum: 1, segmentTrackingByCount: { 1: true } },
			true
		);

		await Promise.all(
			invites.map(async (invite) => {
				if (invite.email) {
					return this.studioInvite(studioId, slug, invite.email, invite.role);
				}
			})
		);
	}

	studioDeleted(studioId, slug, title) {
		return this.track('Studio Deleted', {
			studioId,
			slug,
			title,
		});
	}

	studioRecordStart(studioId, slug, archiveId, sessionId) {
		this.trackProxiedEvent(
			'recording_started',
			{},
			{ trackAfterNum: [1, 3], segmentTrackingByCount: { 1: true } },
			true
		);

		return this.track(
			'Studio Recording Started',
			{
				studioId,
				slug,
				archiveId,
				sessionId,
			},
			{
				context: {
					traits: { made_a_recording: new Date() },
				},
			}
		);
	}

	studioRecordStopped(studioId, slug, archiveId, sessionId, markers_count) {
		return this.track('Studio Recording Stopped', {
			studioId,
			slug,
			archiveId,
			sessionId,
			markers_count,
		});
	}

	studioEntered(studioId, slug, role) {
		return this.track('Studio Entered', {
			studioId,
			slug,
			role,
		});
	}

	studioJoined(studioId, slug, role, connectionId, userName, headphones) {
		return this.track('Studio Join', {
			studioId,
			slug,
			role,
			connectionId,
			userName,
			headphones,
		});
	}

	studioUploadsCompleted(studioId, slug) {
		return this.track('Studio Uploads Completed', {
			studioId,
			slug,
		});
	}

	async studioInvite(studioId, slug, email, role) {
		await this.trackProxiedEvent(
			'invite_participant',
			{ role },
			{ trackAfterNum: 1, segmentTrackingByCount: { 1: true } },
			true
		);

		return this.track(
			'Studio Invite',
			{ studioId, slug, email, role },
			{
				context: {
					traits: { send_out_an_invitation: new Date() },
				},
			}
		);
	}

	studioInviteLink(studioId, slug, link, role) {
		return this.track('Studio Invite Link', { studioId, slug, link, role });
	}

	studioRated(studioId, slug, userName, role, rating) {
		return this.track('Studio Rated', { studioId, slug, userName, role, rating });
	}

	studioFeedback(studioId, slug, userName, role, ratingId, message) {
		return this.track('Studio Feedback', { studioId, slug, userName, role, ratingId, message });
	}

	/* Recordings related events */

	recordingStarted(archiveId, fileName, speakerName, sessionId, recordingType) {
		return this.track('Recording Started', {
			archiveId,
			fileName,
			speakerName,
			sessionId,
			recordingType,
		});
	}

	recordingDownloaded(archiveId, recordingId, label, type, source = null, createdAt) {
		let secondsOld = null;
		if (createdAt) {
			createdAt = new Date(createdAt);
			secondsOld = parseInt((new Date().getTime() - createdAt.getTime()) / 1000);
		}

		this.trackProxiedEvent(
			'file_downloaded',
			{ label, type },
			{ trackAfterNum: 1, segmentTrackingByCount: { 1: true } },
			true
		);

		return this.track(
			'Recording Downloaded',
			{
				archiveId,
				recordingId,
				label,
				type,
				source,
				createdAt,
				secondsOld,
			},
			{
				context: {
					traits: {
						download_files_from_dashboard: new Date(),
					},
				},
			}
		);
	}

	internetRecordingDownloaded(label) {
		this.trackProxiedEvent(
			'file_downloaded',
			{ label, type: 'internet' },
			{ trackAfterNum: 1, segmentTrackingByCount: { 1: true } },
			true
		);
	}

	recordingStopped(archiveId) {
		return this.track('Recording Stopped', { archiveId });
	}
	composerStep(takeId, slug, sessionId, step, info) {
		return this.track('Composer Step', {
			takeId,
			slug,
			sessionId,
			step,
			info,
		});
	}

	composerDownloaded(recordingId, archiveId, type, createdAt) {
		let secondsOld = null;
		if (createdAt) {
			createdAt = new Date(createdAt);
			secondsOld = parseInt((new Date().getTime() - createdAt.getTime()) / 1000);
		}

		return this.track(
			'Composer Downloaded',
			{
				recordingId,
				archiveId,
				type,
				createdAt,
				secondsOld,
			},
			{
				context: {
					traits: {
						download_files_from_dashboard: new Date(),
					},
				},
			}
		);
	}

	recordingFeedbackScore(sessionId, score) {
		return this.track('Recording Feedback Score', {
			sessionId,
			score,
		});
	}

	recordingFeedbackText(sessionId, score, text) {
		return this.track('Recording Feedback Text', {
			sessionId,
			score,
			text,
		});
	}

	userSawFirstTimeLimitMessage(userId, accountId) {
		return this.track('user_reached_free_plan_limit_popup', {
			userId,
			accountId,
		});
	}

	spotifyPublishButtonClicked(userId, clipId, takeId) {
		return this.track('clip_preview_spotify_integration', {
			userId,
			clipId,
			takeId,
		});
	}

	spotifySubmitButtonClicked(userId, clipId, takeId, integrationId) {
		return this.track('spotify_integration_submit', {
			userId,
			clipId,
			takeId,
			integrationId,
		});
	}

	invitedParticipantJoined(studioSlug, role) {
		this.trackProxiedEvent(
			'invited_participant_joined',
			{ role },
			{ trackAfterNum: 1, segmentTrackingByCount: { 1: true } },
			true,
			{ studioSlug }
		);
	}

	greenRoomEntered() {
		this.trackProxiedEvent('green_room_visit', {}, { trackAfterNum: 1, segmentTrackingByCount: { 1: true } });
	}

	liveCallEntered() {
		this.trackProxiedEvent('live_call_entered', {}, { trackAfterNum: 1, segmentTrackingByCount: { 1: true } }, true);
	}

	/*  */

	event(eventName, data) {
		try {
			this.track(eventName, data);
		} catch {}
	}

	fillDataLayerEvent(event) {
		if (!event.email && this.email) {
			event.email = this.email;
		}

		if (!event.name && this.name) {
			event.name = this.name;
		}

		event.fbq = getCookie('_fbp');
	}

	dataLayerTrack(event) {
		try {
			this.fillDataLayerEvent(event);
			window.dataLayer.push(event);
		} catch (e) {
			console.warn('data layer error');
		}
	}

	trackViaGTM(event, data, userId = undefined) {
		try {
			const eventData = { event, ...data };
			if (userId) {
				eventData.userId = userId;
			}
			this.fillDataLayerEvent(eventData);
			if (DISPLAY_LOGS) {
				console.log('[trackViaGTM]', eventData);
			}
			window.dataLayer.push(eventData);
		} catch (e) {
			console.warn('gtm error');
		}
	}

	async trackProxiedEvent(
		event,
		properties,
		{ trackAfterNum, trackAfterProperties = {}, segmentTrackingByCount = {} },
		trackAlways = false,
		identifiers = undefined
	) {
		// console.log('[track proxy]', { event, trackAfterNum, identifiers, trackAlways, properties, trackAfterProperties });
		try {
			// get the updated number of occurrences for this event and the related user id
			await apiV4
				.post(`utilities/update-proxied-analytics-data`, {
					event,
					identifiers,
				})
				.then((res) => {
					if (res.status !== 200) {
						return;
					}
					const { count, userId } = res.data;

					try {
						if (segmentTrackingByCount[count]) {
							this.track(getOrdinalNumber(count) + '_' + event);
						}
					} catch (e) {
						console.log('[track proxy] segment tracking failed');
					}
					// console.log('[track proxy] got', count, 'for', event, 'with user id', userId);

					// track event, check first if we've reached trackAfter condition
					const trackedOccurrence = Array.isArray(trackAfterNum) ? trackAfterNum : [trackAfterNum];
					const logData = { event, count, trackAfterNum };
					// if the event should be tracked at this occurrence, track it
					if (trackAfterNum && trackedOccurrence.find((x) => x === count)) {
						// console.log('[track proxy] track after', logData);
						return this.trackViaGTM(event, { ...properties, occurrence: count, ...trackAfterProperties }, userId);
					}
					// otherwise, if should always be tracked, track it
					else if (trackAlways) {
						// console.log('[track proxy] track always', logData);
						return this.trackViaGTM(event, { ...properties, occurrence: count }, userId);
					} else {
						// console.log('[track proxy] no tracking necessary', logData);
					}
				});
		} catch (e) {}
	}
}

export default new analytics();
